/**
 * The panel beside the login form.
 *
 * A reactor core: a still white centre, a halo breathing around it, and rings
 * travelling outward. The motion is deliberately slow — seven seconds a breath,
 * nine a ring — so it reads as something running rather than something
 * demanding attention. A login screen is the one place in an application where
 * the user is waiting, so it can afford to be looked at.
 *
 * Colours are the application's own indigo, with the aqua taken from the
 * logo's own gradient for the glow. That keeps the reactor reading as a
 * reactor without introducing a colour that appears nowhere else in Core.
 *
 * Everything is sized in vh so the composition holds its proportions at any
 * window height, and the whole thing sits still under prefers-reduced-motion.
 */
export default function CoreLoginPanel() {
  return (
    <div className="relative w-full h-full overflow-hidden flex items-center justify-center bg-[#241F63]">
      <style>{`
        @keyframes coreBreath {
          0%, 100% { transform: scale(1);    opacity: .75; }
          50%      { transform: scale(1.09); opacity: 1; }
        }
        @keyframes coreRing {
          0%   { transform: scale(.55); opacity: 0; }
          15%  { opacity: .45; }
          100% { transform: scale(1.7); opacity: 0; }
        }
        @keyframes coreGlow {
          0%, 100% { box-shadow: 0 0 70px 6px rgba(0,220,255,.40); }
          50%      { box-shadow: 0 0 120px 18px rgba(0,220,255,.62); }
        }
        @media (prefers-reduced-motion: reduce) {
          .core-breath, .core-ring, .core-glow { animation: none !important; }
        }
      `}</style>

      {/* Depth behind the core, so the panel is not a flat block of colour */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 46%, #3B32A0 0%, #2A2478 45%, #1B1750 100%)",
        }}
      />

      {/* Halo */}
      <div
        className="absolute core-breath rounded-full"
        style={{
          width: "62vh",
          height: "62vh",
          background:
            "radial-gradient(circle, rgba(0,220,255,.42) 0%, rgba(99,102,241,.22) 45%, rgba(0,180,255,0) 70%)",
          animation: "coreBreath 7s ease-in-out infinite",
        }}
      />

      {/* Rings travelling outward, offset so one is always on its way */}
      {[0, 3, 6].map((delay) => (
        <div
          key={delay}
          className="absolute core-ring rounded-full"
          style={{
            width: "36vh",
            height: "36vh",
            border: "1px solid rgba(160,235,255,.55)",
            animation: `coreRing 9s ease-out ${delay}s infinite`,
          }}
        />
      ))}

      {/* The containment ring: a fixed edge for the moving ones to cross */}
      <div
        className="absolute rounded-full"
        style={{
          width: "62vh",
          height: "62vh",
          border: "1px solid rgba(255,255,255,.10)",
        }}
      />

      {/* The core itself */}
      <div
        className="absolute core-glow rounded-full"
        style={{
          width: "30vh",
          height: "30vh",
          background:
            "radial-gradient(circle, #fff 0%, #fff 58%, rgba(214,250,255,.92) 100%)",
          animation: "coreGlow 7s ease-in-out infinite",
        }}
      />

      {/* Logo, still, on top of everything that moves */}
      <div className="relative">
        <svg viewBox="0 0 110.35 66.09" style={{ width: "17vh", height: "auto", display: "block" }}>
          <defs>
            <linearGradient id="coreLoginLogo" x1="-4.84" y1="37.68" x2="71.5" y2="33.37" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#00E5FF" />
              <stop offset="1" stopColor="#3730A3" />
            </linearGradient>
          </defs>
          <path
            fill="url(#coreLoginLogo)"
            d="M67,33A10.34,10.34,0,1,0,77.33,22.7,10.35,10.35,0,0,0,67,33Z"
          />
          <path
            fill="url(#coreLoginLogo)"
            d="M66.24,1.91v0l-.12.05c-1.09.4-22,10.25-40,18.75l21.17,10C53.8,27.59,65,22.32,69.85,20a15,15,0,1,1,7.46,28,14.79,14.79,0,0,1-7.19-1.86l-2.28-1C65.35,44.1,39.71,32,21,23.14,9.31,28.63,0,33,0,33s64.18,30.4,66.13,31.11l.11,0v0a33,33,0,1,0,0-62.27Z"
          />
        </svg>
      </div>

      {/* Wordmark */}
      <div
        className="absolute left-0 right-0 text-center text-white"
        style={{
          bottom: "56px",
          fontSize: "32px",
          letterSpacing: ".36em",
          textIndent: ".36em",
          lineHeight: 1,
        }}
      >
        core
      </div>
    </div>
  );
}
